import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronDown,
  Download,
  ExternalLink,
  FileCheck2,
  Loader2,
} from "lucide-react";

import { renderIzjavaPdf } from "@/api/client";
import type { FactSheet } from "@/api/schemas";
import { officePayload, useOfficeStore } from "@/features/projects/useOfficeStore";
import { cn } from "@/lib/utils";

interface IzjavaPdfPreviewProps {
  /** Section id ("izjava_1_3") or slot ("1.3"); the endpoint normalises both. */
  slot: string;
  factSheet: FactSheet;
  /** Merged opći-dio variables the izjava was generated with (uploads/manual). */
  variables?: Record<string, unknown>;
  /**
   * Flattened statement text (``section.content``). Shown as the in-app
   * fallback when the browser refuses to render the PDF inline — some Chromium
   * setups (Edge "always download PDF", disabled PDF viewer) leave an
   * ``<iframe>`` blank with no error. We embed via ``<object>`` so that case
   * degrades to readable text instead of an empty white box.
   */
  fallbackText?: string;
}

/**
 * Office-fidelity izjava preview. The PDF the export bundles (boxed title,
 * office header/footer, signature blocks) is the source of truth, fetched as a
 * Blob (auth is header-based, so a bare ``src`` wouldn't carry the token).
 *
 * Presentation: a clean, on-brand action card by default — "Otvori u novoj
 * kartici" / "Preuzmi PDF" always work — with the full-fidelity render tucked
 * behind an expand toggle. Previously the raw browser PDF viewer (grey chrome,
 * its own toolbar, a nested scroll region) was embedded 900 px tall for every
 * izjava at once; collapsing it removes that visual break while keeping true
 * WYSIWYG one click away.
 */
export function IzjavaPdfPreview({
  slot,
  factSheet,
  variables,
  fallbackText,
}: IzjavaPdfPreviewProps) {
  const office = useOfficeStore((s) => s.office);
  const [expanded, setExpanded] = useState(false);
  const { data: blob, isPending, isError, error } = useQuery({
    queryKey: ["izjava-pdf", slot, variables, office],
    queryFn: () =>
      renderIzjavaPdf({
        slot,
        fact_sheet: factSheet,
        variables,
        office: officePayload(office) ?? undefined,
      }),
    staleTime: Infinity,
    retry: false,
  });

  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) return;
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  if (isPending || (!url && !isError)) {
    return (
      <div className="flex items-center gap-2 rounded-md bg-stone-50 px-4 py-6 text-sm text-stone-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Učitavam izgled izjave…
      </div>
    );
  }

  if (isError || !url) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <span>
          Pregled izjave nije moguće prikazati.
          {error instanceof Error ? ` (${error.message})` : ""}
        </span>
      </div>
    );
  }

  const fileName = `izjava_${slot.replace(/^izjava_/, "").replace(/\./g, "_")}.pdf`;

  return (
    <div className="overflow-hidden rounded-md border border-stone-200">
      {/* On-brand summary bar — replaces the always-on grey PDF chrome. */}
      <div className="flex flex-wrap items-center gap-2 bg-stone-50 px-3 py-2.5">
        <FileCheck2 className="h-4 w-4 flex-shrink-0 text-ind-600" />
        <div className="mr-auto min-w-0">
          <div className="text-[0.82rem] font-semibold text-stone-700">
            Službeni izgled izjave
          </div>
          <div className="text-[0.72rem] text-stone-400">
            Vjerno uredskom predlošku — zaglavlje, okvir naslova i potpisni blok.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-1 rounded-md border border-stone-200 bg-white px-2 py-1 text-[0.8rem] text-stone-600 transition-colors hover:bg-stone-50"
        >
          <ChevronDown
            className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-180")}
          />
          {expanded ? "Sakrij izgled" : "Prikaži izgled"}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 rounded-md border border-stone-200 bg-white px-2 py-1 text-[0.8rem] text-stone-600 transition-colors hover:bg-stone-50"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Otvori
        </a>
        <a
          href={url}
          download={fileName}
          className="inline-flex items-center gap-1 rounded-md border border-stone-200 bg-white px-2 py-1 text-[0.8rem] text-stone-600 transition-colors hover:bg-stone-50"
        >
          <Download className="h-3.5 w-3.5" />
          Preuzmi PDF
        </a>
      </div>

      {expanded && (
        <object
          data={url}
          type="application/pdf"
          aria-label={`Izjava ${slot}`}
          className="h-[600px] w-full border-t border-stone-200 bg-white"
        >
          {/* Rendered only when the browser can't display the PDF inline —
              keeps the section readable instead of blank. */}
          <div className="space-y-3 bg-stone-50 p-4 text-sm text-stone-600">
            <p className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
              Preglednik ne prikazuje PDF ovdje. Otvori ga u novoj kartici ili
              preuzmi gore — ispod je tekst izjave.
            </p>
            {fallbackText && (
              <pre className="whitespace-pre-wrap rounded-md bg-white p-4 font-sans text-[0.88rem] leading-relaxed text-stone-700">
                {fallbackText}
              </pre>
            )}
          </div>
        </object>
      )}
    </div>
  );
}
